// ─────────────────────────────────────────────────────────────
//  Commission-Core – EINZIGE QUELLE DER WAHRHEIT für den
//  BonusClub-Vergütungsplan (gestaffelte Provisionen je Termin).
//
//  Reines CommonJS ohne Abhängigkeiten – genau wie billingCore.js,
//  damit Frontend UND Cloud Functions dieselbe Datei nutzen
//  (Kopie via predeploy-Hook in firebase.json).
//
//  >>> NUR DIESE DATEI BEARBEITEN. functions/commissionCore.js wird
//  >>> beim Deploy überschrieben.
//
//  ── Grundprinzip ──────────────────────────────────────────────
//  Zwei Geldgrößen werden strikt getrennt:
//
//    appointmentPrice  – was der KUNDE je Termin zahlt (Rechnung, netto)
//    commissionBase    – was davon als provisionsfähiger UMSATZ gilt
//
//  Beispiel: Kunde zahlt 390 €/Termin, provisionsfähig sind 260 €.
//  Die Staffel rechnet ausschließlich auf commissionBase; die
//  Firmenmarge ergibt sich aus (appointmentPrice − Auszahlungen).
//
//  Der Plan ist auf COMMISSION_PLAN_BASE (240 €) kalibriert und wird
//  proportional auf die tatsächliche Provisionsbasis skaliert:
//
//    scale = commissionBase / 240 × rollenFaktor
//
//  Die Rollenfaktoren verteilen den Auszahlungstopf auf ca.
//  80 % Outreach-Manager / 20 % Teamleiter.
//
//  Kalibrierung (Referenz: 50 Termine): die Auszahlungen zusammen liegen bei
//  ~45 % des PROVISIONSFÄHIGEN Umsatzes (commissionBase). Gemessen am echten
//  Kundenumsatz ist die Firmenmarge höher, weil die nicht provisionsfähige
//  Differenz vollständig bei der Firma bleibt – bei 390 €/260 € sind das
//  ~70 % bei 50 Terminen und ~80 % bei 10 Terminen. Wenige Termine sind
//  also überproportional profitabel, die Skalierung belohnt Volumen.
// ─────────────────────────────────────────────────────────────

// Referenzpreis, auf den der Vergütungsplan kalibriert ist (EUR/Termin).
const COMMISSION_PLAN_BASE = 240;

// Staffeln: `to: null` = offen nach oben. Die Sätze gelten progressiv,
// d. h. jeder Termin wird mit dem Satz SEINER Stufe vergütet (nicht der
// gesamte Monat rückwirkend mit dem höchsten Satz).
const OUTREACH_TIERS = [
  { from: 1, to: 4, rate: 60 },
  { from: 5, to: 9, rate: 70 },
  { from: 10, to: 14, rate: 80 },
  { from: 15, to: 19, rate: 90 },
  { from: 20, to: 24, rate: 100 },
  { from: 25, to: 29, rate: 110 },
  { from: 30, to: null, rate: 120 },
];

// Cash-Unlocks: Einmalbonus je erreichter Schwelle, kumulativ.
const OUTREACH_UNLOCKS = [
  { at: 5, bonus: 50 },
  { at: 10, bonus: 75 },
  { at: 15, bonus: 100 },
  { at: 20, bonus: 125 },
  { at: 25, bonus: 130 },
  { at: 30, bonus: 150 },
];

const TEAM_LEAD_TIERS = [
  { from: 1, to: 24, rate: 8 },
  { from: 25, to: 49, rate: 10 },
  { from: 50, to: 74, rate: 12 },
  { from: 75, to: 99, rate: 14 },
  { from: 100, to: null, rate: 16 },
];

const TEAM_LEAD_UNLOCKS = [
  { at: 12, bonus: 25 },
  { at: 25, bonus: 50 },
  { at: 37, bonus: 75 },
  { at: 50, bonus: 100 },
  { at: 62, bonus: 125 },
  { at: 75, bonus: 150 },
  { at: 87, bonus: 175 },
  { at: 100, bonus: 200 },
  { at: 125, bonus: 250 },
  { at: 150, bonus: 300 },
];

const COMMISSION_ROLES = {
  outreach_manager: {
    id: 'outreach_manager',
    label: 'Outreach-Manager',
    tierHeading: 'Termine',
    tiers: OUTREACH_TIERS,
    unlocks: OUTREACH_UNLOCKS,
    unlockHeading: 'Cash Unlocks',
    factor: 0.77,
    defaultMinimum: 200,
  },
  team_leader: {
    id: 'team_leader',
    label: 'Teamleiter',
    tierHeading: 'Team-Termine',
    tiers: TEAM_LEAD_TIERS,
    unlocks: TEAM_LEAD_UNLOCKS,
    unlockHeading: 'Unlocks je Account',
    factor: 1.53,
    defaultMinimum: 100,
  },
};

const COMMISSION_ROLE_IDS = ['outreach_manager', 'team_leader'];

const commissionRoleInfo = (roleId) => COMMISSION_ROLES[roleId] || null;

const toCount = (v) => Math.max(0, Math.floor(Number(v) || 0));
const toMoney = (v) => {
  const n = Number(v);
  return isFinite(n) && n > 0 ? n : 0;
};

// Progressive Summe über alle Staffelstufen: jede Stufe vergütet nur die
// Termine, die tatsächlich in ihr Intervall fallen.
function tierSum(count, tiers) {
  const n = toCount(count);
  let sum = 0;
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    if (n < t.from) continue;
    const upper = t.to == null ? n : Math.min(n, t.to);
    const inTier = upper - t.from + 1;
    if (inTier > 0) sum += inTier * t.rate;
  }
  return sum;
}

// Satz der Stufe, in der die aktuelle Terminzahl liegt (für die Anzeige).
function tierRateAt(count, tiers) {
  const n = toCount(count);
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    if (n >= t.from && (t.to == null || n <= t.to)) return t.rate;
  }
  return 0;
}

// Summe aller erreichten Unlock-Schwellen (kumulativ).
function unlockSum(count, unlocks) {
  const n = toCount(count);
  let sum = 0;
  for (let i = 0; i < unlocks.length; i++) {
    if (n >= unlocks[i].at) sum += unlocks[i].bonus;
  }
  return sum;
}

// Provisionsbasis eines Vertrags: der Teil des Terminpreises, auf den die
// Staffel rechnet. Ohne eigene Angabe zählt der volle Kundenpreis.
function commissionBaseFor(contract) {
  if (!contract) return 0;
  if (typeof contract.commissionBase === 'number' && contract.commissionBase >= 0) {
    return contract.commissionBase;
  }
  return toMoney(contract.appointmentPrice);
}

// Was der Kunde je Termin zahlt. Der Vertrag schlägt den (aus dem Research
// abgeleiteten) Fallback-Preis, sobald er intern festgelegt wurde.
function appointmentPriceFor(contract, fallback) {
  const own = contract && Number(contract.appointmentPrice);
  if (isFinite(own) && own > 0) return own;
  return toMoney(fallback);
}

const commissionActive = (contract) => !!(contract && contract.commissionEnabled);

// Rolle, die für dieses Mitglied BEI DIESEM KUNDEN hinterlegt ist.
// Dieselbe Person kann bei Kunde A Outreach Manager und bei Kunde B Teamleiter
// sein – deshalb liegt die Zuordnung am Kunden, nicht am Mitglied.
function customerRoleOverride(customer, memberId) {
  const roles = customer && customer.teamRoles;
  if (!roles || !memberId) return null;
  const v = roles[memberId];
  return typeof v === 'string' && v ? v : null;
}

// Welche Staffel gilt für dieses Mitglied? Reihenfolge der Vorrangs:
//   1. `override` – die Rolle, die am Kunden gewählt wurde
//   2. `member.commissionRole` – globale Vorgabe aus den Team-Settings
//   3. Ableitung aus der Positionsbezeichnung
// 'none' = bewusst keine Staffel; das Mitglied bleibt beim klassischen Modell
// (Fix/Prozent + Pauschalbonus).
function commissionRoleForMember(member, override) {
  if (!member) return null;
  if (typeof override === 'string' && override) {
    return COMMISSION_ROLES[override] ? override : null;
  }
  const explicit = member.commissionRole;
  if (typeof explicit === 'string' && explicit) {
    return COMMISSION_ROLES[explicit] ? explicit : null;
  }
  const role = String(member.role || '').toLowerCase();
  if (role.indexOf('outreach') !== -1) return 'outreach_manager';
  if (/team\s*-?\s*(leiter|lead)/.test(role)) return 'team_leader';
  return null;
}

// Garantiertes Minimum je Abrechnung. Ohne eigene Angabe gilt der im
// Vergütungsplan hinterlegte Standardwert der Rolle.
function memberCommissionMinimum(member, roleId) {
  const raw = member && member.commissionMin;
  if (typeof raw === 'number' && isFinite(raw) && raw >= 0) return raw;
  const role = commissionRoleInfo(roleId);
  return role ? role.defaultMinimum : 0;
}

// Kernrechnung: Provision einer Rolle bei `appointments` Terminen.
// Alle Beträge in EUR, netto.
function commissionForRole(roleId, appointments, commissionBase, minimum) {
  const role = commissionRoleInfo(roleId);
  if (!role) return null;
  const count = toCount(appointments);
  const base = toMoney(commissionBase);
  const scale = (base / COMMISSION_PLAN_BASE) * role.factor;
  const tierAmount = tierSum(count, role.tiers) * scale;
  const unlockAmount = unlockSum(count, role.unlocks) * scale;
  const computed = tierAmount + unlockAmount;
  // Das Minimum ist eine Untergrenze der Abrechnung, keine Grundvergütung:
  // ohne einen einzigen Termin im Monat entsteht auch keine Garantie.
  const min = count > 0 ? toMoney(minimum) : 0;
  const amount = Math.max(computed, min);
  return {
    roleId: role.id,
    roleLabel: role.label,
    appointments: count,
    commissionBase: base,
    scale,
    tierAmount,
    unlockAmount,
    computed,
    minimum: min,
    minimumApplied: amount > computed + 0.005,
    amount,
    ratePerAppointment: tierRateAt(count, role.tiers) * scale,
  };
}

// Provision EINES Mitglieds an EINEM Kunden bei `appointments` Terminen.
// null, wenn der Vertrag kein Provisionsmodell hat oder das Mitglied keiner
// Staffel zugeordnet ist – dann greift weiter die klassische Vergütung.
function commissionForMember(member, contract, appointments, roleOverride) {
  if (!commissionActive(contract)) return null;
  if (!member || member.active === false) return null;
  const roleId = commissionRoleForMember(member, roleOverride);
  if (!roleId) return null;
  return commissionForRole(roleId, appointments, commissionBaseFor(contract), memberCommissionMinimum(member, roleId));
}

// Komplette Aufteilung einer Abrechnung – die Grundlage für Rechner,
// Vorschau in der Pipeline und die Monatsabrechnung.
//
//   participants: [{ id, name, roleId, minimum }]
//
// Liefert Kundenumsatz, provisionsfähigen Umsatz, jede Einzelprovision und
// die verbleibende Firmenmarge (gemessen am ECHTEN Kundenumsatz).
function commissionBreakdown(input) {
  const opts = input || {};
  const appointments = toCount(opts.appointments);
  const appointmentPrice = toMoney(opts.appointmentPrice);
  const commissionBase = toMoney(opts.commissionBase);
  const participants = Array.isArray(opts.participants) ? opts.participants : [];

  const shares = [];
  for (let i = 0; i < participants.length; i++) {
    const p = participants[i] || {};
    const minimum = typeof p.minimum === 'number' ? p.minimum : memberCommissionMinimum(null, p.roleId);
    const calc = commissionForRole(p.roleId, appointments, commissionBase, minimum);
    if (!calc) continue;
    shares.push(Object.assign({ id: p.id || p.roleId, name: p.name || calc.roleLabel }, calc));
  }

  const customerRevenue = appointments * appointmentPrice;
  const commissionRevenue = appointments * commissionBase;
  let payoutTotal = 0;
  for (let i = 0; i < shares.length; i++) payoutTotal += shares[i].amount;
  const companyAmount = customerRevenue - payoutTotal;

  return {
    appointments,
    appointmentPrice,
    commissionBase,
    customerRevenue,
    commissionRevenue,
    // Differenz zwischen Kundenpreis und Provisionsbasis – der Teil, der nie
    // in die Staffel einfließt (Fremdkosten, Tooling, Lead-Einkauf …).
    nonCommissionable: customerRevenue - commissionRevenue,
    shares,
    payoutTotal,
    companyAmount,
    companyPct: customerRevenue > 0 ? (companyAmount / customerRevenue) * 100 : 0,
    payoutPct: customerRevenue > 0 ? (payoutTotal / customerRevenue) * 100 : 0,
  };
}

module.exports = {
  COMMISSION_PLAN_BASE,
  COMMISSION_ROLES,
  COMMISSION_ROLE_IDS,
  OUTREACH_TIERS,
  OUTREACH_UNLOCKS,
  TEAM_LEAD_TIERS,
  TEAM_LEAD_UNLOCKS,
  commissionRoleInfo,
  tierSum,
  tierRateAt,
  unlockSum,
  commissionActive,
  commissionBaseFor,
  appointmentPriceFor,
  commissionRoleForMember,
  customerRoleOverride,
  memberCommissionMinimum,
  commissionForRole,
  commissionForMember,
  commissionBreakdown,
};
